import { useState } from 'react';
import { LitSquadInventoryEntry } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { resolveLitSquadDisplayName } from '../utils';

interface TransferItemDialogProps {
  item: LitSquadInventoryEntry | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (toUsername: string) => Promise<void>;
}

export function TransferItemDialog({ item, open, onClose, onConfirm }: TransferItemDialogProps) {
  const [toUsername, setToUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setToUsername('');
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!toUsername.trim()) {
      setError('Recipient username is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(toUsername.trim());
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const title = item ? resolveLitSquadDisplayName(item) : '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transfer {title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="litsquad-to-username">Recipient Username</Label>
            <Input
              id="litsquad-to-username"
              value={toUsername}
              onChange={(e) => setToUsername(e.target.value)}
              placeholder="username"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Transferring...' : 'Transfer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
